import React, { useState, useEffect } from 'react';
import { getGIMonthlySummary, recalculateGIMonth, markGIPayoutPaid } from '../../model/api.js';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';

export const GIMonthlySummarySubTab = () => {
    const [selectedMonth, setSelectedMonth] = useState('');
    const [loading, setLoading] = useState(false);
    const [recalculating, setRecalculating] = useState(false);
    const [summary, setSummary] = useState(null);
    const [applications, setApplications] = useState([]);
    const [payoutSummary, setPayoutSummary] = useState([]);
    const [payouts, setPayouts] = useState([]);

    // Mark as paid modal state
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [selectedUserPayouts, setSelectedUserPayouts] = useState(null);
    const [transactionId, setTransactionId] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('Bank Transfer');
    const [paymentNotes, setPaymentNotes] = useState('');
    const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
    const [marking, setMarking] = useState(false);

    // View breakdown modal state
    const [showBreakdownModal, setShowBreakdownModal] = useState(false);
    const [breakdownData, setBreakdownData] = useState(null);

    // Set default month to current month
    useEffect(() => {
        const now = new Date();
        const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        setSelectedMonth(defaultMonth);
    }, []);

    // Fetch data when month changes
    useEffect(() => {
        if (selectedMonth) {
            fetchMonthlySummary();
        }
    }, [selectedMonth]);

    const fetchMonthlySummary = async () => {
        setLoading(true);
        try {
            const result = await getGIMonthlySummary(selectedMonth);
            if (result.ok) {
                setSummary(result.data.summary);
                setApplications(result.data.applications);
                setPayoutSummary(result.data.payout_summary);
                setPayouts(result.data.payouts || []);
            }
        } catch (error) {
            toast.error('Error fetching monthly summary: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleRecalculate = async () => {
        if (!window.confirm(`Recalculate commissions for ${selectedMonth}? This will delete all unpaid payouts and recreate them.`)) {
            return;
        }

        setRecalculating(true);
        try {
            const result = await recalculateGIMonth(selectedMonth);
            if (result.ok) {
                toast.success(result.message || 'Commissions recalculated successfully!');
                fetchMonthlySummary(); // Refresh data
            }
        } catch (error) {
            toast.error('Error recalculating commissions: ' + error.message);
        } finally {
            setRecalculating(false);
        }
    };

    const handleMarkAsPaidClick = (userPayout) => {
        // Get all UNPAID payouts for this user
        const userPayoutRecords = payouts.filter(p => p.user_id?._id === userPayout.user._id && p.status === 'unpaid');

        if (userPayoutRecords.length === 0) {
            toast.info('All payouts for this user are already paid.');
            return;
        }

        setSelectedUserPayouts(userPayoutRecords);
        setPaymentDate(new Date().toISOString().split('T')[0]); // Reset date to today
        setShowPaymentModal(true);
    };

    const handleMarkAsPaid = async () => {
        if (!transactionId.trim()) {
            toast.warning('Please enter transaction ID/UTR');
            return;
        }
        console.log(selectedUserPayouts);

        setMarking(true);
        try {
            const paymentData = {
                transaction_id: transactionId,
                payment_method: paymentMethod,
                payment_notes: paymentNotes,
                payment_date: paymentDate
            };

            // Mark all payouts for this user as paid
            for (const payout of selectedUserPayouts) {
                console.log('Sending pay request for', payout._id, paymentData);
                await markGIPayoutPaid(payout._id, paymentData);
            }

            toast.success(`Successfully marked ${selectedUserPayouts.length} payout(s) as paid!`);
            setShowPaymentModal(false);
            setTransactionId('');
            setPaymentMethod('Bank Transfer');
            setPaymentNotes('');
            setPaymentDate(new Date().toISOString().split('T')[0]);
            setSelectedUserPayouts(null);
            fetchMonthlySummary(); // Refresh data
        } catch (error) {
            console.error('Mark paid error:', error);
            const errMsg = error.response?.data?.error || error.response?.data?.message || error.message;
            toast.error('Error marking as paid: ' + errMsg);
        } finally {
            setMarking(false);
        }
    };

    const handleViewBreakdown = (userPayout) => {
        // Get all payouts for this user
        const userPayoutRecords = payouts.filter(p => p.user_id?._id === userPayout.user._id);
        setBreakdownData({
            user: userPayout.user,
            role: userPayout.role,
            level: userPayout.hierarchy_level,
            payouts: userPayoutRecords
        });
        setShowBreakdownModal(true);
    };

    const formatCurrency = (amount) => {
        return `₹${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const formatMonthDisplay = (monthStr) => {
        if (!monthStr) return '';
        const [year, month] = monthStr.split('-');
        const date = new Date(year, parseInt(month) - 1);
        return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    };

    const downloadExcel = () => {
        if (!summary || !applications || !payouts) {
            toast.warning('No data available to download');
            return;
        }

        try {
            // Create workbook
            const wb = XLSX.utils.book_new();

            // Sheet 1: Summary
            const summaryData = [
                ['General Insurance Monthly Summary'],
                ['Month', formatMonthDisplay(selectedMonth)],
                [''],
                ['Total Applications', summary.total_applications],
                ['Total Policy Amount', summary.total_policy_amount],
                ['Total Commission', summary.total_commission],
                ['Total Payouts', summary.total_payouts],
                ['Paid Payouts', summary.paid_payouts],
                ['Unpaid Payouts', summary.unpaid_payouts]
            ];
            const ws1 = XLSX.utils.aoa_to_sheet(summaryData);
            XLSX.utils.book_append_sheet(wb, ws1, 'Summary');

            // Sheet 2: Applications
            const applicationsData = applications.map(app => ({
                'Application ID': app.application_id,
                'Customer Name': app.customer_name,
                'Customer Phone': app.customer_phone,
                'Insurance Type': app.insurance_type,
                'Policy Amount': app.policy_amount,
                'Commission %': app.commission_percentage,
                'Commission Amount': app.commission_amount,
                'Hierarchy %': app.hierarchy_percentage,
                'Submitted By': app.advisor_id?.name || 'N/A',
                'Role': app.submitted_by_role,
                'Approved Date': app.approved_at ? new Date(app.approved_at).toLocaleDateString() : 'N/A'
            }));
            const ws2 = XLSX.utils.json_to_sheet(applicationsData);
            XLSX.utils.book_append_sheet(wb, ws2, 'Applications');

            // Sheet 3: All Payouts
            const payoutsData = payouts.map(payout => ({
                'User Name': payout.user_id?.name || 'N/A',
                'User Phone': payout.user_id?.phone || 'N/A',
                'Role': payout.user_id?.role || 'N/A',
                'Application ID': payout.application_id?.application_id || 'N/A',
                'Customer': payout.application_id?.customer_name || 'N/A',
                'Policy Amount': payout.application_id?.policy_amount || 0,
                'Payout Amount': payout.payout_amount,
                'Hierarchy Level': payout.hierarchy_level,
                'Status': payout.status,
                'Payment Method': payout.payment_method || 'N/A',
                'Transaction ID': payout.transaction_id || 'N/A',
                'Paid Date': payout.paid_at ? new Date(payout.paid_at).toLocaleDateString() : 'N/A'
            }));
            const ws3 = XLSX.utils.json_to_sheet(payoutsData);
            XLSX.utils.book_append_sheet(wb, ws3, 'All Payouts');

            // Sheet 4: Payout Summary by User
            const payoutSummaryData = payoutSummary.map(item => ({
                'User Name': item.user.name,
                'User Phone': item.user.phone,
                'Role': item.role,
                'Hierarchy Level': item.hierarchy_level,
                'Total Payout': item.total_payout,
                'Paid Amount': item.paid_amount,
                'Unpaid Amount': item.unpaid_amount,
                'Number of Policies': item.policy_count,
                'Payment Status': item.paid_amount === item.total_payout ? 'Fully Paid' : 'Pending'
            }));
            const ws4 = XLSX.utils.json_to_sheet(payoutSummaryData);
            XLSX.utils.book_append_sheet(wb, ws4, 'Payout Summary');

            // Generate file name
            const fileName = `GI_Commission_${selectedMonth}.xlsx`;

            // Download
            XLSX.writeFile(wb, fileName);
            toast.success('Excel file downloaded successfully!');
        } catch (error) {
            console.error('Error generating Excel:', error);
            toast.error('Error generating Excel file');
        }
    };

    return (
        <div style={{ padding: '20px' }}>
            <h2 style={{ marginBottom: '20px', fontSize: '24px', fontWeight: 'bold' }}>
                GI Monthly Summary & Commission Calculation
            </h2>

            {/* Month Selector */}
            <div style={{ marginBottom: '30px', display: 'flex', gap: '15px', alignItems: 'center' }}>
                <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                        Select Month:
                    </label>
                    <input
                        type="month"
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                        style={{
                            padding: '8px 12px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '14px'
                        }}
                    />
                </div>
                <div style={{ marginTop: '20px' }}>
                    <button
                        onClick={fetchMonthlySummary}
                        disabled={loading}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#4f46e5',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            fontSize: '14px',
                            marginRight: '10px'
                        }}
                    >
                        {loading ? 'Loading...' : 'View Details'}
                    </button>
                    {summary && (
                        <button
                            onClick={downloadExcel}
                            style={{
                                padding: '8px 16px',
                                backgroundColor: '#10b981',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '14px'
                            }}
                        >
                            📥 Download Excel
                        </button>
                    )}
                </div>
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <p>Loading monthly summary...</p>
                </div>
            ) : summary ? (
                <>
                    {/* Summary Cards */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '20px',
                        marginBottom: '30px'
                    }}>
                        <div style={{
                            padding: '20px',
                            backgroundColor: '#f0f9ff',
                            borderRadius: '8px',
                            border: '1px solid #bae6fd'
                        }}>
                            <h3 style={{ fontSize: '14px', color: '#0369a1', marginBottom: '8px' }}>Total Policies</h3>
                            <p style={{ fontSize: '28px', fontWeight: 'bold', color: '#0c4a6e' }}>
                                {summary.total_policies}
                            </p>
                        </div>
                        <div style={{
                            padding: '20px',
                            backgroundColor: '#f0fdf4',
                            borderRadius: '8px',
                            border: '1px solid #bbf7d0'
                        }}>
                            <h3 style={{ fontSize: '14px', color: '#15803d', marginBottom: '8px' }}>Total Amount</h3>
                            <p style={{ fontSize: '28px', fontWeight: 'bold', color: '#14532d' }}>
                                {formatCurrency(summary.total_amount)}
                            </p>
                        </div>
                        <div style={{
                            padding: '20px',
                            backgroundColor: '#fef3c7',
                            borderRadius: '8px',
                            border: '1px solid #fde68a'
                        }}>
                            <h3 style={{ fontSize: '14px', color: '#a16207', marginBottom: '8px' }}>Total Commission</h3>
                            <p style={{ fontSize: '28px', fontWeight: 'bold', color: '#713f12' }}>
                                {formatCurrency(summary.total_commission)}
                            </p>
                        </div>
                    </div>

                    {/* Policy Details Table */}
                    <div style={{ marginBottom: '30px' }}>
                        <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '15px' }}>
                            Policy Details for {formatMonthDisplay(selectedMonth)}
                        </h3>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{
                                width: '100%',
                                borderCollapse: 'collapse',
                                backgroundColor: 'white',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                            }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>Application ID</th>
                                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>Advisor</th>
                                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>Leader</th>
                                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>Type</th>
                                        <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>Amount</th>
                                        <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>Commission %</th>
                                        <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>Commission</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {applications.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                                                No policies found for this month
                                            </td>
                                        </tr>
                                    ) : (
                                        applications.map((app, index) => (
                                            <tr key={app._id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                                <td style={{ padding: '12px', fontSize: '13px', fontFamily: 'monospace' }}>{app.application_id}</td>
                                                <td style={{ padding: '12px', fontSize: '13px' }}>{app.advisor_id?.name || 'N/A'}</td>
                                                <td style={{ padding: '12px', fontSize: '13px' }}>{app.leader_id?.name || 'N/A'}</td>
                                                <td style={{ padding: '12px', fontSize: '13px', textTransform: 'capitalize' }}>{app.insurance_type}</td>
                                                <td style={{ padding: '12px', fontSize: '13px', textAlign: 'right', fontWeight: '500' }}>
                                                    {formatCurrency(app.policy_amount)}
                                                </td>
                                                <td style={{ padding: '12px', fontSize: '13px', textAlign: 'right' }}>
                                                    {app.commission_percentage}%
                                                </td>
                                                <td style={{ padding: '12px', fontSize: '13px', textAlign: 'right', fontWeight: '500', color: '#059669' }}>
                                                    {formatCurrency(app.commission_amount)}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Recalculate Button */}
                    <div style={{ marginBottom: '30px' }}>
                        <button
                            onClick={handleRecalculate}
                            disabled={recalculating || applications.length === 0}
                            style={{
                                padding: '12px 24px',
                                backgroundColor: recalculating ? '#9ca3af' : '#dc2626',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: recalculating || applications.length === 0 ? 'not-allowed' : 'pointer',
                                fontSize: '14px',
                                fontWeight: '500'
                            }}
                        >
                            {recalculating ? 'Recalculating...' : '🔄 Recalculate Commissions for This Month'}
                        </button>
                        <p style={{ marginTop: '8px', fontSize: '12px', color: '#6b7280' }}>
                            This will delete all unpaid payouts and recreate them based on current commission settings
                        </p>
                    </div>

                    {/* Payout Summary Table */}
                    <div>
                        <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '15px' }}>
                            Payout Summary by User
                        </h3>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{
                                width: '100%',
                                borderCollapse: 'collapse',
                                backgroundColor: 'white',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                            }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>User</th>
                                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>Role</th>
                                        <th style={{ padding: '12px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>Level</th>
                                        <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>Payout Count</th>
                                        <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>Total Payout</th>
                                        <th style={{ padding: '12px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>Status</th>
                                        <th style={{ padding: '12px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {payoutSummary.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                                                No payouts calculated yet. Click "Recalculate Commissions" to generate payouts.
                                            </td>
                                        </tr>
                                    ) : (
                                        payoutSummary.map((payout, index) => (
                                            <tr key={index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                                <td style={{ padding: '12px', fontSize: '13px', fontWeight: '500' }}>
                                                    {payout.user?.name || 'N/A'}
                                                </td>
                                                <td style={{ padding: '12px', fontSize: '13px', textTransform: 'capitalize' }}>
                                                    {payout.role}
                                                </td>
                                                <td style={{ padding: '12px', fontSize: '13px', textAlign: 'center' }}>
                                                    L{payout.hierarchy_level}
                                                </td>
                                                <td style={{ padding: '12px', fontSize: '13px', textAlign: 'right' }}>
                                                    {payout.payout_count}
                                                </td>
                                                <td style={{ padding: '12px', fontSize: '13px', textAlign: 'right', fontWeight: '600', color: '#059669' }}>
                                                    {formatCurrency(payout.total_payout)}
                                                </td>
                                                <td style={{ padding: '12px', textAlign: 'center' }}>
                                                    <span style={{
                                                        padding: '4px 12px',
                                                        borderRadius: '12px',
                                                        fontSize: '11px',
                                                        fontWeight: '500',
                                                        backgroundColor: payout.status === 'paid' ? '#d1fae5' : '#fef3c7',
                                                        color: payout.status === 'paid' ? '#065f46' : '#92400e'
                                                    }}>
                                                        {payout.status}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '12px', textAlign: 'center' }}>
                                                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                                        <button
                                                            onClick={() => handleViewBreakdown(payout)}
                                                            style={{
                                                                padding: '6px 12px',
                                                                backgroundColor: '#3b82f6',
                                                                color: 'white',
                                                                border: 'none',
                                                                borderRadius: '4px',
                                                                cursor: 'pointer',
                                                                fontSize: '12px',
                                                                fontWeight: '500'
                                                            }}
                                                        >
                                                            View
                                                        </button>
                                                        {payout.status === 'unpaid' && (
                                                            <button
                                                                onClick={() => handleMarkAsPaidClick(payout)}
                                                                style={{
                                                                    padding: '6px 12px',
                                                                    backgroundColor: '#10b981',
                                                                    color: 'white',
                                                                    border: 'none',
                                                                    borderRadius: '4px',
                                                                    cursor: 'pointer',
                                                                    fontSize: '12px',
                                                                    fontWeight: '500'
                                                                }}
                                                            >
                                                                Mark as Paid
                                                            </button>
                                                        )}
                                                        {payout.status === 'paid' && (
                                                            <span style={{ fontSize: '12px', color: '#6b7280' }}>✓ Paid</span>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Commission Breakdown Modal */}
                    {showBreakdownModal && breakdownData && (
                        <div style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 1000
                        }}>
                            <div style={{
                                backgroundColor: 'white',
                                borderRadius: '8px',
                                padding: '24px',
                                maxWidth: '700px',
                                width: '90%',
                                maxHeight: '90vh',
                                overflow: 'auto'
                            }}>
                                <h3 style={{ marginBottom: '20px', fontSize: '18px', fontWeight: 'bold' }}>
                                    Commission Breakdown - {breakdownData.user?.name}
                                </h3>

                                <div style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#f3f4f6', borderRadius: '6px' }}>
                                    <p style={{ fontSize: '14px', marginBottom: '4px' }}>
                                        <strong>Role:</strong> {breakdownData.role} (Level {breakdownData.level})
                                    </p>
                                    <p style={{ fontSize: '14px', marginBottom: '4px' }}>
                                        <strong>Total Payouts:</strong> {breakdownData.payouts.length}
                                    </p>
                                    <p style={{ fontSize: '14px' }}>
                                        <strong>Total Amount:</strong> {formatCurrency(breakdownData.payouts.reduce((sum, p) => sum + p.payout_amount, 0))}
                                    </p>
                                </div>

                                <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
                                    Individual Payout Calculations:
                                </h4>

                                {breakdownData.payouts.map((payout, index) => (
                                    <div key={index} style={{
                                        marginBottom: '16px',
                                        padding: '16px',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '6px',
                                        backgroundColor: '#fafafa'
                                    }}>
                                        <p style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                                            Application: {payout.application_id?.application_id || 'N/A'}
                                        </p>

                                        {/* Payment Details for Paid Payouts */}
                                        {payout.status === 'paid' && (
                                            <div style={{
                                                marginBottom: '12px',
                                                padding: '12px',
                                                backgroundColor: '#d1fae5',
                                                borderRadius: '4px',
                                                border: '1px solid #10b981'
                                            }}>
                                                <p style={{ fontSize: '13px', fontWeight: '600', color: '#065f46', marginBottom: '6px' }}>
                                                    ✓ Payment Details
                                                </p>
                                                <div style={{ fontSize: '12px', color: '#065f46' }}>
                                                    {payout.transaction_id && (
                                                        <p><strong>Transaction ID/UTR:</strong> {payout.transaction_id}</p>
                                                    )}
                                                    {payout.payment_method && (
                                                        <p><strong>Payment Method:</strong> {payout.payment_method}</p>
                                                    )}
                                                    {payout.payment_notes && (
                                                        <p><strong>Notes:</strong> {payout.payment_notes}</p>
                                                    )}
                                                    {payout.paid_at && (
                                                        <p><strong>Paid On:</strong> {new Date(payout.paid_at).toLocaleDateString('en-IN', {
                                                            day: '2-digit',
                                                            month: 'short',
                                                            year: 'numeric',
                                                            hour: '2-digit',
                                                            minute: '2-digit'
                                                        })}</p>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                                            <p><strong>Policy Amount:</strong> {formatCurrency(payout.policy_amount)}</p>
                                            <p><strong>Commission Rate:</strong> {payout.commission_percentage}%</p>
                                            <p><strong>Total Commission:</strong> {formatCurrency(payout.total_commission)}</p>

                                            {breakdownData.role === 'advisor' ? (
                                                <>
                                                    <p style={{ marginTop: '8px', color: '#6b7280' }}>
                                                        <em>Calculation: {formatCurrency(payout.policy_amount)} × {payout.commission_percentage}% = {formatCurrency(payout.total_commission)}</em>
                                                    </p>
                                                    <p style={{ marginTop: '4px', color: '#6b7280' }}>
                                                        <em>After leader commissions ({payout.hierarchy_percentage}% cascade): {formatCurrency(payout.payout_amount)}</em>
                                                    </p>
                                                </>
                                            ) : (
                                                <>
                                                    <p><strong>Hierarchy Share:</strong> {payout.hierarchy_percentage}%</p>
                                                    <p><strong>Base Amount:</strong> {formatCurrency(payout.base_amount)}</p>
                                                    <p style={{ marginTop: '8px', color: '#6b7280' }}>
                                                        <em>Calculation: {formatCurrency(payout.base_amount)} × {payout.hierarchy_percentage}% = {formatCurrency(payout.payout_amount)}</em>
                                                    </p>
                                                </>
                                            )}

                                            <p style={{ marginTop: '12px', fontSize: '14px', fontWeight: '600', color: payout.status === 'paid' ? '#10b981' : '#059669' }}>
                                                Your Payout: {formatCurrency(payout.payout_amount)} {payout.status === 'paid' && '✓'}
                                            </p>
                                        </div>
                                    </div>
                                ))}

                                <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
                                    <button
                                        onClick={() => {
                                            setShowBreakdownModal(false);
                                            setBreakdownData(null);
                                        }}
                                        style={{
                                            padding: '8px 16px',
                                            backgroundColor: '#3b82f6',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            fontWeight: '500'
                                        }}
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Payment Modal */}
                    {showPaymentModal && selectedUserPayouts && (
                        <div style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 1000
                        }}>
                            <div style={{
                                backgroundColor: 'white',
                                borderRadius: '8px',
                                padding: '24px',
                                maxWidth: '500px',
                                width: '90%',
                                maxHeight: '90vh',
                                overflow: 'auto'
                            }}>
                                <h3 style={{ marginBottom: '20px', fontSize: '18px', fontWeight: 'bold' }}>
                                    Mark Payouts as Paid
                                </h3>

                                <div style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#f3f4f6', borderRadius: '6px' }}>
                                    <p style={{ fontSize: '14px', marginBottom: '4px' }}>
                                        <strong>User:</strong> {selectedUserPayouts[0]?.user_id?.name}
                                    </p>
                                    <p style={{ fontSize: '14px', marginBottom: '4px' }}>
                                        <strong>Total Amount:</strong> {formatCurrency(selectedUserPayouts.reduce((sum, p) => sum + p.payout_amount, 0))}
                                    </p>
                                    <p style={{ fontSize: '14px' }}>
                                        <strong>Payouts:</strong> {selectedUserPayouts.length}
                                    </p>
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                                        Payment Date *
                                    </label>
                                    <input
                                        type="date"
                                        value={paymentDate}
                                        onChange={(e) => setPaymentDate(e.target.value)}
                                        required
                                        style={{
                                            width: '100%',
                                            padding: '8px 12px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '4px',
                                            fontSize: '14px'
                                        }}
                                    />
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                                        Transaction ID / UTR *
                                    </label>
                                    <input
                                        type="text"
                                        value={transactionId}
                                        onChange={(e) => setTransactionId(e.target.value)}
                                        placeholder="Enter transaction ID or UTR number"
                                        style={{
                                            width: '100%',
                                            padding: '8px 12px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '4px',
                                            fontSize: '14px'
                                        }}
                                    />
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                                        Payment Method
                                    </label>
                                    <select
                                        value={paymentMethod}
                                        onChange={(e) => setPaymentMethod(e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '8px 12px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '4px',
                                            fontSize: '14px'
                                        }}
                                    >
                                        <option value="Bank Transfer">Bank Transfer</option>
                                        <option value="UPI">UPI</option>
                                        <option value="Cash">Cash</option>
                                        <option value="Cheque">Cheque</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>

                                <div style={{ marginBottom: '20px' }}>
                                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                                        Payment Notes (Optional)
                                    </label>
                                    <textarea
                                        value={paymentNotes}
                                        onChange={(e) => setPaymentNotes(e.target.value)}
                                        placeholder="Add any additional notes about this payment"
                                        rows="3"
                                        style={{
                                            width: '100%',
                                            padding: '8px 12px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '4px',
                                            fontSize: '14px',
                                            resize: 'vertical'
                                        }}
                                    />
                                </div>

                                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                                    <button
                                        onClick={() => {
                                            setShowPaymentModal(false);
                                            setTransactionId('');
                                            setPaymentMethod('Bank Transfer');
                                            setPaymentNotes('');
                                            setSelectedUserPayouts(null);
                                        }}
                                        disabled={marking}
                                        style={{
                                            padding: '8px 16px',
                                            backgroundColor: '#e5e7eb',
                                            color: '#374151',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: marking ? 'not-allowed' : 'pointer',
                                            fontSize: '14px',
                                            fontWeight: '500'
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleMarkAsPaid}
                                        disabled={marking}
                                        style={{
                                            padding: '8px 16px',
                                            backgroundColor: marking ? '#9ca3af' : '#10b981',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: marking ? 'not-allowed' : 'pointer',
                                            fontSize: '14px',
                                            fontWeight: '500'
                                        }}
                                    >
                                        {marking ? 'Processing...' : 'Confirm Payment'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            ) : (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                    <p>Select a month to view policy details and commission summary</p>
                </div>
            )}
        </div>
    );
};
